import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { navLinks } from "../../mock";
import { SERVICES } from "../../data/site";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dentalDropdownOpen, setDentalDropdownOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <nav
      className={`bg-[#F6EEDC] border-y border-[#D8C29A]/20 transition-shadow ${
        scrolled ? "shadow-[0_8px_24px_-12px_rgba(184,148,104,0.15)]" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="hidden lg:flex items-center justify-between">
          {navLinks.map((link) => {
            const active = pathname === link.path;
            // Check if this is the Dental Services link
            const isDentalServices = link.label === "Dental Services";
            
            if (isDentalServices) {
              return (
                <div key={link.label} className="relative group">
                  <Link
                    to="/services"
                    className={`font-cinzel uppercase text-[12px] tracking-[0.18em] py-5 relative transition-colors after:content-[''] after:absolute after:bottom-3 after:left-1/2 after:-translate-x-1/2 after:h-px after:bg-[#D8C29A] after:transition-all flex items-center gap-1 ${
                      active
                        ? "text-[#B89468] after:w-8"
                        : "text-[#3A3127]/85 hover:text-[#9A7A52] after:w-0 hover:after:w-6"
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </Link>
                  {/* Dropdown menu */}
                  <div className="absolute left-0 mt-0 w-max min-w-[280px] bg-[#ffffff] border border-[#D8C29A]/24 rounded-sm shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    {SERVICES.map((service) => (
                      <Link
                        key={service.slug}
                        to={`/services/${service.slug}`}
                        className="block px-6 py-3.5 text-sm text-[#3A3127] hover:text-[#9A7A52] hover:bg-[#F6EEDC] border-b border-[#D8C29A]/10 last:border-0 transition-colors"
                      >
                        <div className="font-cinzel uppercase text-[11px] tracking-[0.15em] text-[#B89468]">{service.name}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }
            
            return (
              <Link
                key={link.label}
                to={link.path}
                className={`font-cinzel uppercase text-[12px] tracking-[0.18em] py-5 relative transition-colors after:content-[''] after:absolute after:bottom-3 after:left-1/2 after:-translate-x-1/2 after:h-px after:bg-[#D8C29A] after:transition-all ${
                  active
                    ? "text-[#B89468] after:w-8"
                    : "text-[#3A3127]/85 hover:text-[#9A7A52] after:w-0 hover:after:w-6"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="lg:hidden flex items-center justify-between py-4">
          <span className="font-cinzel text-[#B89468] text-sm tracking-[0.3em] uppercase">
            Menu
          </span>
          <button
            className="text-[#B89468]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-4">
            <ul className="flex flex-col gap-2">
              {navLinks.map((link) => {
                const isDentalServices = link.label === "Dental Services";
                
                if (isDentalServices) {
                  return (
                    <li key={link.label}>
                      <div className="w-full flex items-center justify-between py-2.5 px-2 font-cinzel uppercase text-[12px] tracking-[0.2em] text-[#3A3127]/85 border-b border-[#D8C29A]/15">
                        <Link
                          to="/services"
                          onClick={() => setOpen(false)}
                          className="hover:text-[#9A7A52]"
                        >
                          {link.label}
                        </Link>
                        <button
                          onClick={() => setDentalDropdownOpen(!dentalDropdownOpen)}
                          aria-label="Toggle dental services submenu"
                          className="hover:text-[#9A7A52]"
                        >
                          <ChevronDown size={14} className={`transition-transform ${dentalDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                      {dentalDropdownOpen && (
                        <div className="bg-[#F6EEDC] border-b border-[#D8C29A]/15">
                          {SERVICES.map((service) => (
                            <Link
                              key={service.slug}
                              to={`/services/${service.slug}`}
                              onClick={() => {
                                setOpen(false);
                                setDentalDropdownOpen(false);
                              }}
                              className="block py-2.5 px-4 ml-4 text-sm text-[#3A3127] hover:text-[#9A7A52] border-b border-[#D8C29A]/10 last:border-0 transition-colors"
                            >
                              <div className="font-cinzel uppercase text-[10px] tracking-[0.15em] text-[#B89468]">{service.name}</div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                }
                
                return (
                  <li key={link.label}>
                    <Link
                      to={link.path}
                      onClick={() => setOpen(false)}
                      className="block py-2.5 px-2 font-cinzel uppercase text-[12px] tracking-[0.2em] text-[#3A3127]/85 border-b border-[#D8C29A]/15 hover:text-[#9A7A52]"
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
